/**
 * U14 F6: a Maton (https://docs.maton.ai), gateway que guarda e renova o OAuth de apps externos e repassa a chamada à API
 * do app (`https://api.maton.ai/{app}/{caminho}`, conta escolhida pelo cabeçalho `Maton-Connection`). Aqui ficam as regras
 * iguais no servidor e na tela:
 * - **Ação em app externo:** só os apps e as chamadas da lista (`OPERACOES_MATON`); nada de apagar (sem DELETE, sem
 *   `:clear`, `batchUpdate`, `archive` ou `purge`). As variáveis do fluxo entram no caminho (codificadas) e no corpo (dentro
 *   das aspas do JSON).
 * - **Evento de app externo:** as fontes e os eventos que a Maton entrega por webhook (`FONTES_GATILHO_MATON`) e as
 *   variáveis que o evento leva ao fluxo (`variaveisDoEvento`).
 *
 * Sem dependência externa (o pacote é usado pelo web e pelos módulos).
 */

export const MATON_API = 'https://api.maton.ai';
/** Tipo do conector da Maton em Integrações > Conectores. */
export const TIPO_MATON = 'maton';

/**
 * A base da API (gateway e gestão de conexões e gatilhos). `MATON_API_BASE` (Maton simulada nos testes e no ambiente local)
 * só vale fora da produção.
 */
export function baseMaton(env: Record<string, string | undefined>): string {
  const simulada = env.NODE_ENV !== 'production' ? env.MATON_API_BASE?.trim().replace(/\/+$/, '') : '';
  return simulada || MATON_API;
}

/** Apps da ação (começo: Google Sheets e HubSpot; ficha da U14, "Maton AI"). */
export const APPS_ACAO_MATON = ['google-sheets', 'hubspot'] as const;
export type AppAcaoMaton = (typeof APPS_ACAO_MATON)[number];
/** Métodos aceitos no gateway (DELETE nunca). */
export const METODOS_MATON = ['GET', 'POST', 'PUT', 'PATCH'] as const;
export type MetodoMaton = (typeof METODOS_MATON)[number];

export const LIMITES_MATON = {
  caminho: 300,
  consulta: 300,
  corpo: 4000,
  /** A Maton aceita 10 requisições por segundo por conta. */
  porSegundo: 10,
  /** Variáveis que um evento leva ao fluxo (o resto do conteúdo fica em `evento_json`). */
  variaveisEvento: 30,
} as const;

const ID = '[A-Za-z0-9_-]{1,200}';
/** Intervalo do Sheets (`Página1!A1:C1`), já codificado: letras, números, `!`, `:`, `'`, `.`, `_`, `~`, `-` e `%XX`. */
const INTERVALO = "(?:[A-Za-z0-9!:'._~-]|%[0-9A-Fa-f]{2}){1,300}";
const OBJETOS_HUBSPOT = '(contacts|companies|deals|tickets|notes|tasks)';

interface OperacaoMaton {
  id: string;
  metodo: MetodoMaton;
  caminho: RegExp;
}

/**
 * Chamadas permitidas por app (o caminho sem a consulta, já com as variáveis trocadas). Ler, criar, acrescentar e atualizar;
 * nenhuma que apague (limpar intervalo e `batchUpdate` do Sheets podem apagar linhas; `archive` e `purge` do HubSpot apagam).
 */
export const OPERACOES_MATON: Record<AppAcaoMaton, readonly OperacaoMaton[]> = {
  'google-sheets': [
    {
      id: 'ler_planilha',
      metodo: 'GET',
      caminho: new RegExp(`^/v4/spreadsheets/${ID}$`),
    },
    {
      id: 'ler_intervalo',
      metodo: 'GET',
      caminho: new RegExp(`^/v4/spreadsheets/${ID}/values/${INTERVALO}$`),
    },
    {
      id: 'acrescentar_linha',
      metodo: 'POST',
      caminho: new RegExp(`^/v4/spreadsheets/${ID}/values/${INTERVALO}:append$`),
    },
    {
      id: 'atualizar_intervalo',
      metodo: 'PUT',
      caminho: new RegExp(`^/v4/spreadsheets/${ID}/values/${INTERVALO}$`),
    },
  ],
  hubspot: [
    {
      id: 'criar_objeto',
      metodo: 'POST',
      caminho: new RegExp(`^/crm/v3/objects/${OBJETOS_HUBSPOT}$`),
    },
    {
      id: 'buscar_objetos',
      metodo: 'POST',
      caminho: new RegExp(`^/crm/v3/objects/${OBJETOS_HUBSPOT}/search$`),
    },
    {
      id: 'ler_objetos',
      metodo: 'GET',
      caminho: new RegExp(`^/crm/v3/objects/${OBJETOS_HUBSPOT}(/${ID})?$`),
    },
    {
      id: 'atualizar_objeto',
      metodo: 'PATCH',
      caminho: new RegExp(`^/crm/v3/objects/${OBJETOS_HUBSPOT}/${ID}$`),
    },
    {
      id: 'associar',
      metodo: 'PUT',
      caminho: new RegExp(`^/crm/v4/objects/${OBJETOS_HUBSPOT}/${ID}/associations/default/${OBJETOS_HUBSPOT}/${ID}$`),
    },
  ],
};

/** Trechos que apagam (ou podem apagar) em qualquer app, recusados mesmo que um padrão acima deixasse passar. */
const APAGA = /(:clear|:batchclear|:batchupdate|\/archive|\/purge|\/gdpr-delete|\/delete|\/batch\/)/i;

/**
 * Modelos que a tela oferece para preencher a caixa (o caminho e o corpo continuam editáveis e passam pela mesma regra).
 * `{planilha}` e `{intervalo}` são para trocar pelo id da planilha e pela aba.
 */
export const MODELOS_MATON: readonly {
  id: string;
  app: AppAcaoMaton;
  metodo: MetodoMaton;
  caminho: string;
  corpo: string;
}[] = [
  {
    id: 'sheets_linha',
    app: 'google-sheets',
    metodo: 'POST',
    caminho: '/v4/spreadsheets/ID_DA_PLANILHA/values/A1:append?valueInputOption=USER_ENTERED',
    corpo: '{"values": [["{{nome}}", "{{email}}", "{{telefone}}"]]}',
  },
  {
    id: 'hubspot_contato',
    app: 'hubspot',
    metodo: 'POST',
    caminho: '/crm/v3/objects/contacts',
    corpo: '{"properties": {"email": "{{email}}", "firstname": "{{nome}}", "phone": "{{telefone}}"}}',
  },
  {
    id: 'hubspot_negocio',
    app: 'hubspot',
    metodo: 'POST',
    caminho: '/crm/v3/objects/deals',
    corpo: '{"properties": {"dealname": "{{nome}}", "pipeline": "default", "dealstage": "appointmentscheduled"}}',
  },
];

const RE_VAR = /\{\{\s*([a-z][a-z0-9_]{0,39})\s*\}\}/g;
const RE_CONSULTA = /^[A-Za-z0-9_=&.,%-]*$/;

export type ProblemaMaton = [codigo: string, mensagem: string];

/** Acento, espaço e outros fora do ASCII visível viram `%XX` (a aba "Página 1" do Sheets, digitada como se lê). */
export const normalizarCaminhoMaton = (caminho: string) => caminho.replace(/[^\x21-\x7e]/gu, (c) => encodeURIComponent(c));

/**
 * Confere app, método e caminho (com a consulta). `modelo`: na tela e ao publicar, as variáveis `{{nome}}` valem por um
 * texto qualquer; na execução o caminho chega já com os valores codificados.
 */
export function problemaChamadaMaton(app: string, metodo: string, caminhoCompleto: string, modelo = true): ProblemaMaton | null {
  if (!(APPS_ACAO_MATON as readonly string[]).includes(app)) return ['caixa.app_maton', 'Escolha o app (Google Sheets ou HubSpot)'];
  if (!(METODOS_MATON as readonly string[]).includes(metodo)) return ['caixa.metodo_maton', 'Método não permitido (apagar não é permitido)'];
  if (typeof caminhoCompleto !== 'string' || !caminhoCompleto.startsWith('/') || caminhoCompleto.length > LIMITES_MATON.caminho + LIMITES_MATON.consulta + 1) {
    return ['caixa.caminho_maton', 'Informe o caminho da API do app, começando por /'];
  }
  const texto = normalizarCaminhoMaton(modelo ? caminhoCompleto.replace(RE_VAR, 'x1') : caminhoCompleto);
  const [caminho = '', consulta = '', ...resto] = texto.split('?');
  if (resto.length || caminho.length > LIMITES_MATON.caminho || consulta.length > LIMITES_MATON.consulta || !RE_CONSULTA.test(consulta)) {
    return ['caixa.caminho_maton', 'Caminho ou consulta inválidos'];
  }
  // Sem volta de pasta, barra dupla, barra ou ponto codificados (o gateway normaliza o caminho).
  if (/(^|\/)\.\.?(\/|$)|\/\/|\\|%2f|%5c|%2e/i.test(caminho) || /\{\{|\}\}/.test(caminho)) return ['caixa.caminho_maton', 'Caminho inválido'];
  if (APAGA.test(caminho)) return ['caixa.caminho_apaga', 'Chamada que apaga dados não é permitida'];
  const ops = OPERACOES_MATON[app as AppAcaoMaton];
  if (!ops.some((o) => o.metodo === metodo && o.caminho.test(caminho))) return ['caixa.caminho_fora_lista', 'Chamada fora da lista permitida para este app'];
  return null;
}

/** Corpo em JSON com as variáveis dentro das aspas; vazio no GET. */
export function problemaCorpoMaton(metodo: string, corpo: string | null | undefined): ProblemaMaton | null {
  const c = (corpo ?? '').trim();
  if (metodo === 'GET') return c ? ['caixa.corpo_get', 'Chamada GET vai sem corpo'] : null;
  if (!c) return ['caixa.corpo_json', 'Escreva o corpo em JSON'];
  if (c.length > LIMITES_MATON.corpo) return ['caixa.corpo_longo', `O corpo vai até ${LIMITES_MATON.corpo} caracteres`];
  try {
    const v = JSON.parse(c.replace(RE_VAR, 'x'));
    if (!v || typeof v !== 'object') return ['caixa.corpo_json', 'O corpo é um objeto ou uma lista em JSON'];
  } catch {
    return ['caixa.corpo_json', 'Corpo em JSON inválido: as variáveis vão dentro de aspas, como "{{nome}}"'];
  }
  return null;
}

/** Caminho com as variáveis trocadas pelos valores codificados (a regra é conferida de novo, sem modelo). */
export function montarCaminhoMaton(caminho: string, variaveis: Readonly<Record<string, string>>): string {
  return normalizarCaminhoMaton(caminho).replace(RE_VAR, (_m, nome: string) => encodeURIComponent(variaveis[nome] ?? ''));
}

/** Corpo com as variáveis trocadas pelo valor escapado para dentro de uma string JSON. */
export function montarCorpoMaton(corpo: string, variaveis: Readonly<Record<string, string>>): string {
  return corpo.replace(RE_VAR, (_m, nome: string) => JSON.stringify(variaveis[nome] ?? '').slice(1, -1));
}

/** Variáveis que a resposta guarda: o corpo (até 1.000 caracteres) e, quando a resposta tem `id`, `<variavel>_id`. */
export function variaveisDaResposta(nome: string | null | undefined, texto: string, max = 1000): Record<string, string> {
  if (!nome) return {};
  const v: Record<string, string> = { [nome]: texto.slice(0, max) };
  try {
    const j = JSON.parse(texto) as Record<string, unknown>;
    const id = j && typeof j === 'object' && !Array.isArray(j) ? j.id : null;
    if ((typeof id === 'string' || typeof id === 'number') && nome.length <= 37) v[`${nome}_id`] = String(id).slice(0, max);
  } catch {
    // resposta sem JSON: só o texto
  }
  return v;
}

// ---------------------------------------------------------------------------
// Gatilho: eventos da Maton
// ---------------------------------------------------------------------------

/** Fontes e eventos dos gatilhos da Maton que o MalgoOS oferece (documentação, "Maton Events" de cada app). */
export const FONTES_GATILHO_MATON = {
  hubspot: [
    'contact.created',
    'contact.updated',
    'contact.deleted',
    'company.created',
    'company.updated',
    'company.deleted',
    'deal.created',
    'deal.updated',
    'deal.deleted',
    'deal.stage_changed',
    'ticket.created',
    'ticket.updated',
    'ticket.deleted',
  ],
  'google-mail': ['email.received'],
} as const satisfies Record<string, readonly string[]>;
export type FonteGatilhoMaton = keyof typeof FONTES_GATILHO_MATON;
export const FONTES_MATON = Object.keys(FONTES_GATILHO_MATON) as FonteGatilhoMaton[];

export const eventoDaFonte = (fonte: string, evento: string) => (FONTES_GATILHO_MATON as Record<string, readonly string[]>)[fonte]?.includes(evento) ?? false;

/** Ids da Maton (conexão e gatilho). */
export const RE_ID_MATON = /^[A-Za-z0-9_-]{1,100}$/;

/** Exemplo de evento para o "Simular" (o formato que a Maton entrega de cada fonte). */
export function eventoDeExemplo(fonte: string, evento: string): Record<string, unknown> {
  if (fonte === 'google-mail')
    return {
      id: '18c1f0a2b3',
      threadId: '18c1f0a2b3',
      snippet: 'Olá, quero um orçamento',
      labelIds: ['INBOX'],
    };
  return {
    eventId: 60775291,
    subscriptionType: evento,
    objectId: 508369434347,
    portalId: 48975575,
    occurredAt: 1782371969536,
    changeSource: 'INTEGRATION',
  };
}

const nomeVariavel = (partes: string[]) =>
  `evento_${partes
    .join('_')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}`.slice(0, 40);

/**
 * Variáveis que o evento leva ao fluxo: `app_fonte`, `app_evento`, os campos simples do conteúdo (até dois níveis, como
 * `evento_object_id` ou `evento_properties_email`) e o conteúdo inteiro em `evento_json` (até 1.000 caracteres).
 */
export function variaveisDoEvento(fonte: string, evento: string, conteudo: unknown, max = 1000): Record<string, string> {
  const v: Record<string, string> = {
    app_fonte: fonte.slice(0, 60),
    app_evento: evento.slice(0, 60),
  };
  let n = 0;
  const visitar = (o: unknown, partes: string[]) => {
    if (!o || typeof o !== 'object' || Array.isArray(o)) return;
    for (const [k, valor] of Object.entries(o as Record<string, unknown>)) {
      if (n >= LIMITES_MATON.variaveisEvento) return;
      if (valor && typeof valor === 'object' && !Array.isArray(valor)) {
        if (partes.length < 1) visitar(valor, [...partes, k]);
        continue;
      }
      if (typeof valor !== 'string' && typeof valor !== 'number' && typeof valor !== 'boolean') continue;
      const nome = nomeVariavel([...partes, k]);
      if (nome === 'evento_' || nome in v || nome === 'evento_json') continue;
      v[nome] = String(valor).slice(0, max);
      n++;
    }
  };
  visitar(conteudo, []);
  try {
    v.evento_json = JSON.stringify(conteudo ?? null).slice(0, max);
  } catch {
    v.evento_json = '';
  }
  return v;
}

/**
 * Corpo que o MalgoOS pede à Maton em cada entrega do gatilho (`body_template` do destino): os ids do gatilho e do evento,
 * a fonte, o tipo e o conteúdo original do app em `payload`.
 */
export const CORPO_ENTREGA_MATON =
  '{"maton": {"trigger_id": "{{ trigger_id }}", "event_id": "{{ event_id }}", "source": "{{ source }}", "event_type": "{{ event_type }}", "received_at": "{{ received_at }}"}, "payload": {{ payload }}}';
